import React from "react";
import { isChunkError, attemptChunkReload } from "@/lib/chunkGuard";
import { LC_SHIELD_PDF_LOGO } from "@/lib/pdfLogo";

// App-wide Error Boundary. On any render/navigation error it shows a lightweight,
// self-styled (inline CSS, no Tailwind dependency) fallback instead of a blank screen.
// Chunk-load errors trigger a single guarded auto-reload to silently recover.
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    if (isChunkError(error?.message)) {
      attemptChunkReload();
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const s = {
      wrap: {
        minHeight: "100vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", background: "#0B1120",
        color: "#E2E8F0", fontFamily: "system-ui, -apple-system, sans-serif",
        padding: 24, textAlign: "center",
      },
      logo: { height: 60, marginBottom: 24, opacity: 0.95 },
      spin: {
        width: 26, height: 26, borderRadius: "50%",
        border: "3px solid rgba(56,189,248,0.25)", borderTopColor: "#38BDF8",
        animation: "lcSpin 0.8s linear infinite", marginBottom: 22,
      },
      h1: { fontSize: 20, fontWeight: 600, margin: "0 0 8px", color: "#fff" },
      p: { fontSize: 14, color: "#94A3B8", maxWidth: 380, margin: "0 0 22px", lineHeight: 1.6 },
      btn: {
        cursor: "pointer", background: "#38BDF8", color: "#0B1120", border: "none",
        borderRadius: 4, padding: "10px 22px", fontSize: 14, fontWeight: 600,
      },
    };

    return (
      <div style={s.wrap} data-testid="app-error-fallback">
        <img src={LC_SHIELD_PDF_LOGO} alt="LC Shield" style={s.logo} />
        <div style={s.spin} />
        <h1 style={s.h1}>Just a moment…</h1>
        <p style={s.p}>
          We hit a brief snag loading the page. Reconnecting — if this persists, tap reload.
        </p>
        <button style={s.btn} onClick={() => window.location.reload()} data-testid="error-reload-btn">
          Reload
        </button>
        <style>{`@keyframes lcSpin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }
}

export default ErrorBoundary;
